import {graphql, useStaticQuery} from 'gatsby'
import React from 'react'

import {webinarsWatchNowctaActioned} from '@/analytics/segment'
import {EventCard} from '@/components/event-card'
import Image from '@/components/image'
import LocalizedLink from '@/components/localized-link'
import {useLocalizedContent} from '@/hooks/use-localized-content'
import useParams from '@/hooks/use-params'
import locales from '@/locale/locales'
import {useLocale} from '@/providers/locale'
import {CardsGridWithFilters} from '@/sections/cards-grid-with-filters'
import {getLanguageName} from '@/utils/get-language-name'

import type PrismicLink from '@/components/prismic-link'
import type {OnDemandResourcePagesQuery} from '@/graphql-types'
import type {OnDemandResourcesSliceFragment} from '@/graphql-types'
import type {LanguageCode} from '@/locale/locales'
import type {CardLayout} from '@/sections/cards-grid-with-filters'

const TITLE_PARAM = 'webinar-title'
const URL_FILTER_PREFIX = 'webinars-grid'

export const OnDemandResourcesSlice = ({primary}: OnDemandResourcesSliceFragment) => {
    const {allPrismicOnDemandResourcePage} = useStaticQuery<OnDemandResourcePagesQuery>(query)

    const currentLocale = useLocale()
    const params = useParams()

    let hasLocalContent = false
    const resources = useLocalizedContent(allPrismicOnDemandResourcePage.nodes, {
        fallbackToDefaultLocale: false,
    }).map(({data, uid, lang}) => {
        if (data.language_code === currentLocale.language && !hasLocalContent) {
            hasLocalContent = true
        }

        return {
            title: data.title,
            description: data.description,
            category: data.category.document.data.name,
            language:
                getLanguageName(data.language_code as LanguageCode, currentLocale.language) || '',
            duration: data.duration,
            Image: () => <Image image={data.title_image} />,
            CTA: (props: React.ComponentProps<typeof PrismicLink>) => (
                <LocalizedLink
                    {...props}
                    locale={locales.find((locale) => locale.code === lang)?.urlCode}
                    to={`/webinars/${uid}`}
                    onClick={() =>
                        webinarsWatchNowctaActioned({
                            name: data.title,
                        })
                    }
                >
                    {primary.resource_cta_text}
                </LocalizedLink>
            ),
        }
    })

    return (
        <CardsGridWithFilters
            title={primary.title}
            topLink={primary.top_link}
            headLayout="compact"
            topLinkText={primary.top_link_text}
            cardsLayout={primary.layout as CardLayout}
            urlFilterPrefix={URL_FILTER_PREFIX}
            filters={[
                {
                    name: 'category',
                    placeholder: primary.filter_resource_type_placeholder,
                    allLabel: primary.filter_resource_type_all,
                },
                {
                    name: 'language',
                    placeholder: primary.filter_language_placeholder,
                    allLabel: primary.filter_language_all,
                },
            ]}
            initialFilterValues={{
                language: hasLocalContent ? currentLocale.languageName : '',
                category: params.get(`${URL_FILTER_PREFIX}-category`) || '',
            }}
            initialSearchTerm={params.get(TITLE_PARAM)}
            showingAllResultsLabel={primary.showing_all_results}
            showingResultsForLabel={primary.showing_all_results_for}
            clearText={primary.filter_clear}
            noResultsFoundText={primary.no_resources_found}
            searchPlaceholder={primary.search_placeholder}
        >
            {resources.map((resource, index) => (
                <EventCard key={index} {...resource} />
            ))}
        </CardsGridWithFilters>
    )
}

export const onDemandResourcesSliceFragment = graphql`
    fragment OnDemandResourcesSlice on PrismicSlicePageDataBodyOnDemandResources {
        __typename
        primary {
            title
            top_link_text
            top_link {
                ...PrismicLink
            }
            layout
            resource_cta_text
            showing_all_results
            showing_all_results_for
            filter_resource_type_placeholder
            filter_resource_type_all
            filter_language_placeholder
            filter_language_all
            filter_clear
            no_resources_found
            search_placeholder
        }
    }
`

export const query = graphql`
    query OnDemandResourcePages($imageBreakpoints: [Int], $threeColumns: String) {
        allPrismicOnDemandResourcePage {
            nodes {
                _previewable
                uid
                lang
                type
                last_publication_date
                data {
                    title
                    title_image {
                        alt
                        copyright
                        dimensions {
                            height
                            width
                        }
                        url
                        gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $threeColumns)
                    }
                    duration
                    description
                    category {
                        document {
                            ... on PrismicOnDemandResourceCategory {
                                id
                                data {
                                    name
                                }
                            }
                        }
                    }
                    language_code
                }
            }
        }
    }
`
